import { useEffect, useRef, useState } from 'react'
import {
  TrendingUp, Clock, XCircle, FileWarning,
  Activity, MapPin, Tag, AlertOctagon,
} from 'lucide-react'
import type { DashboardKPIs } from '@/types'

// Animated counter
function useAnimatedNumber(target: number, duration = 1000) {
  const [value, setValue] = useState(0)
  const animRef = useRef<number | null>(null)
  useEffect(() => {
    const startTime = performance.now()
    const tick = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setValue(eased * target)
      if (progress < 1) animRef.current = requestAnimationFrame(tick)
    }
    animRef.current = requestAnimationFrame(tick)
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current) }
  }, [target, duration])
  return value
}

interface KPICardProps {
  title:     string
  value:     number
  suffix?:   string
  subtitle?: string
  icon:      React.ComponentType<{ className?: string }>
  iconColor: string
  iconBg:    string
  decimals?: number
  highlight?: 'danger' | 'warn' | 'ok'
}

function KPICard({ title, value, suffix = '', subtitle, icon: Icon, iconColor, iconBg, decimals = 0, highlight }: KPICardProps) {
  const anim = useAnimatedNumber(value)
  const highlightCls = highlight === 'danger' ? 'ring-1 ring-red-200' : highlight === 'warn' ? 'ring-1 ring-yellow-200' : ''
  return (
    <div className={`card p-5 ${highlightCls}`}>
      <div className="flex items-start justify-between mb-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider leading-tight">{title}</p>
        <div className={`rounded-xl p-2.5 ${iconBg} shrink-0 ml-2`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
      </div>
      <p className="text-3xl font-bold text-gray-900 leading-none tabular-nums">
        {anim.toFixed(decimals)}{suffix}
      </p>
      {subtitle && <p className="text-xs text-gray-400 mt-1.5">{subtitle}</p>}
    </div>
  )
}

// Activity state mini-bar card
function ActivityStateCard({ kpis }: { kpis: DashboardKPIs }) {
  const total = kpis.total_activity_instances || 1
  const bars = [
    { label: 'No iniciada', count: kpis.not_started,  color: '#9CA3AF' },
    { label: 'En curso',    count: kpis.in_progress,  color: '#FACC15' },
    { label: 'Finalizada',  count: kpis.completed,    color: '#2563EB' },
    { label: 'Bloqueada',   count: kpis.blocked,      color: '#DC2626' },
  ]
  return (
    <div className="card p-5">
      <div className="flex items-start justify-between mb-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Estado actividades</p>
        <div className="rounded-xl p-2.5 bg-slate-50 shrink-0 ml-2"><Activity className="w-5 h-5 text-slate-600" /></div>
      </div>
      <p className="text-3xl font-bold text-gray-900 leading-none mb-2">{kpis.total_activity_instances}</p>
      <div className="h-2 w-full rounded-full overflow-hidden flex mb-3">
        {bars.map(b => (
          <div key={b.label} style={{ width: `${(b.count / total) * 100}%`, backgroundColor: b.color, minWidth: b.count > 0 ? 3 : 0 }} />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-y-1 gap-x-2">
        {bars.map(b => (
          <div key={b.label} className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: b.color }} />
            <span className="text-xs text-gray-500 truncate">{b.label}</span>
            <span className="text-xs font-semibold text-gray-900 ml-auto">{b.count}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// Skeleton loader
function KPICardSkeleton() {
  return (
    <div className="card p-5 animate-pulse">
      <div className="flex justify-between mb-3">
        <div className="h-3 w-28 bg-gray-200 rounded" />
        <div className="w-10 h-10 bg-gray-200 rounded-xl" />
      </div>
      <div className="h-9 w-20 bg-gray-200 rounded mb-2" />
      <div className="h-3 w-32 bg-gray-200 rounded" />
    </div>
  )
}

// =============================================================================
// Main KPICards export
// =============================================================================

export default function KPICards({ kpis, loading }: { kpis: DashboardKPIs | null; loading: boolean }) {
  if (loading || !kpis) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-6 gap-4">
        {Array.from({ length: 6 }).map((_, i) => <KPICardSkeleton key={i} />)}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">

      {/* Global progress */}
      <div className="card p-5">
        <div className="flex items-start justify-between mb-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Avance global planta</p>
          <div className="rounded-xl p-2.5 bg-blue-50 shrink-0 ml-2"><TrendingUp className="w-5 h-5 text-blue-600" /></div>
        </div>
        <p className="text-4xl font-bold text-gray-900 leading-none">
          {kpis.global_progress_pct.toFixed(1)}<span className="text-2xl font-semibold text-gray-400">%</span>
        </p>
        <div className="mt-2.5 h-2 w-full bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full bg-blue-600 transition-all duration-1000" style={{ width: `${kpis.global_progress_pct}%` }} />
        </div>
        <p className="text-xs text-gray-400 mt-1.5">{kpis.total_activity_instances} instancias de actividad</p>
      </div>

      {/* Activity state breakdown */}
      <ActivityStateCard kpis={kpis} />

      {/* PPIs pendientes */}
      <KPICard title="PPIs pendientes" value={kpis.pending_documents} subtitle="Documentos por revisar"
        icon={FileWarning} iconColor="text-yellow-600" iconBg="bg-yellow-50"
        highlight={kpis.pending_documents > 10 ? 'warn' : undefined} />

      {/* Validaciones pendientes */}
      <KPICard title="Validaciones pendientes" value={kpis.pending_validations} subtitle="Esperando aprobación"
        icon={Clock} iconColor="text-orange-600" iconBg="bg-orange-50"
        highlight={kpis.pending_validations > 20 ? 'warn' : undefined} />

      {/* Actividades bloqueadas */}
      <KPICard title="Actividades bloqueadas" value={kpis.blocked} subtitle="Requieren atención urgente"
        icon={XCircle} iconColor="text-red-600" iconBg="bg-red-50"
        highlight={kpis.blocked > 0 ? 'danger' : undefined} />

      {/* Trackers etiquetados */}
      <div className="card p-5">
        <div className="flex items-start justify-between mb-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Trackers etiquetados</p>
          <div className="rounded-xl p-2.5 bg-green-50 shrink-0 ml-2"><Tag className="w-5 h-5 text-green-600" /></div>
        </div>
        <p className="text-3xl font-bold text-gray-900 leading-none">
          {kpis.labeled_trackers.toLocaleString('es-ES')}
          <span className="text-base font-medium text-gray-400"> / {kpis.total_trackers.toLocaleString('es-ES')}</span>
        </p>
        <div className="mt-2.5 h-2 w-full bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full bg-green-500 transition-all duration-1000"
            style={{ width: `${(kpis.labeled_trackers / kpis.total_trackers) * 100}%` }} />
        </div>
        <p className="text-xs text-gray-400 mt-1.5">{kpis.review_queue_count} en cola de revisión</p>
      </div>

    </div>
  )
}
